import React,{useCallback,useEffect,useState} from "react";
import {api} from "../api";
import {useUser} from "../UserContext";

// Registered-member conversation; intentionally separate from published-story Live Feedback.
export default function GeneralDiscussionsV1({lang="en"}) {
  const fr=String(lang).toLowerCase().startsWith("fr");
  const {isWriter,isAdmin}=useUser();
  const canPost=Boolean(isWriter||isAdmin);
  const [open,setOpen]=useState(false);
  const [threads,setThreads]=useState([]);
  const [before,setBefore]=useState("");
  const [busy,setBusy]=useState(false);
  const [posting,setPosting]=useState(false);
  const [error,setError]=useState("");
  const [draft,setDraft]=useState("");
  const [replyId,setReplyId]=useState("");
  const [replyText,setReplyText]=useState("");
  const load=useCallback(async({older=false,cursor=""}={})=>{
    setBusy(true);setError("");
    try{
      const result=await api("/api/feedback/general"+(older?"?before="+encodeURIComponent(cursor):""),{method:"GET"});
      if(!result?.ok||!Array.isArray(result.threads))throw new Error("Unavailable");
      const rows=result.threads;
      setThreads(prev=>older?[...prev,...rows.filter(r=>!prev.some(x=>x.id===r.id))]:rows);
      setBefore(typeof result.nextBefore==="string"?result.nextBefore:"");
    }catch{setError(fr?"Discussions momentanément indisponibles.":"Discussions temporarily unavailable.");}
    finally{setBusy(false);}
  },[fr]);
  useEffect(()=>{if(open)void load();},[open,load]);
  const post=async(text,parentId="")=>{
    if(!canPost||posting||!text.trim())return;
    setPosting(true);setError("");
    try {
      await api("/api/feedback/general",{
        method:"POST",body:JSON.stringify({text:text.trim(),parentId})
      });
      setDraft("");setReplyText("");setReplyId("");await load();
    }catch{setError(fr?"Impossible d'envoyer votre message.":"Could not submit your message.");}
    finally{setPosting(false);}
  };
  return <details className="ns-card" data-general-discussions-v1="true"
    onToggle={e=>setOpen(e.currentTarget.open)}
    style={{marginTop:14,padding:"16px 18px",borderRadius:16}}>
    <summary style={{cursor:"pointer",fontWeight:800,color:"#065f46",fontSize:17}}>
      {fr?"Discussions générales":"General Discussions"}
    </summary>
    {open&&<div style={{display:"grid",gap:12,marginTop:15}}>
      <p className="ns-card-meta" style={{margin:0}}>
        {fr?"Échanges généraux entre membres connectés. Ces messages ne sont pas publiés sur la page d’accueil.":"General conversation between signed-in members. These posts are not published on the public homepage."}
      </p>
      {!canPost&&<p className="ns-card-meta">
        {fr?"Lecture seule. Seuls les auteurs autorisés peuvent participer.":"Read-only. Only authorised writers can post or reply."}
      </p>}
      <div style={{display:"flex",justifyContent:"flex-end"}}>
        <button type="button" className="ns-button ns-button-outline" disabled={busy}
          onClick={()=>void load()}>{fr?"Actualiser":"Refresh"}</button>
      </div>
      {error&&<p role="alert" style={{color:"#a33d38"}}>{error}</p>}
      <div role="region" tabIndex={0}
        aria-label={fr?"Liste des discussions générales":"General discussion threads"}
        style={{maxHeight:480,overflowY:"auto",overscrollBehavior:"contain",display:"grid",gap:10,scrollBehavior:"smooth"}}>
        {!busy&&threads.length===0&&<p className="ns-card-meta">{fr?"Aucune discussion pour le moment.":"No discussions yet."}</p>}
        {threads.map(thread=><article key={thread.id} className="ns-card ns-card-soft" style={{padding:14}}>
          <div className="ns-card-meta"><strong>{thread.author}</strong>{" · "}
            {thread.createdAt?new Date(thread.createdAt).toLocaleString(fr?"fr-FR":"en-GB"):""}</div>
          <p style={{whiteSpace:"pre-wrap",overflowWrap:"anywhere",lineHeight:1.5}}>{thread.text}</p>
          {(thread.replies||[]).map(reply=><div key={reply.id}
            style={{padding:"9px 12px",borderLeft:"2px solid #b6ddc4",margin:"8px 0 8px 12px"}}>
            <strong>{reply.author}</strong>
            <p style={{whiteSpace:"pre-wrap",overflowWrap:"anywhere",margin:"4px 0"}}>{reply.text}</p>
          </div>)}
          {canPost&&!thread.isOwn&&<button type="button" className="ns-button ns-button-outline"
            onClick={()=>{setReplyId(replyId===thread.id?"":thread.id);setReplyText("");}}>
            {replyId===thread.id?(fr?"Fermer":"Close"):(fr?"Répondre":"Reply")}
          </button>}
          {canPost&&!thread.isOwn&&replyId===thread.id&&<form onSubmit={e=>{e.preventDefault();void post(replyText,thread.id)}} style={{marginTop:10}}>
            <textarea className="ws-textarea ws-feedback-input" maxLength={2000} rows={3}
              aria-label={fr?"Écrire une réponse":"Write a reply"} value={replyText}
              onChange={e=>setReplyText(e.target.value)} />
            <button type="submit" className="ns-button ns-button-primary" disabled={posting||!replyText.trim()}>
              {fr?"Envoyer la réponse":"Post reply"}</button>
          </form>}
        </article>)}
      </div>
      {before&&<button type="button" className="ns-button ns-button-outline" disabled={busy}
        onClick={()=>void load({older:true,cursor:before})}>
        {fr?"Charger les discussions précédentes":"Load older discussions"}
      </button>}
      {canPost&&<form onSubmit={e=>{e.preventDefault();void post(draft)}} style={{borderTop:"1px solid #d9e7dd",paddingTop:12}}>
        <label style={{fontWeight:700,display:"block",marginBottom:8}} htmlFor="ns-general-discussion-draft">
          {fr?"Partager une réflexion":"Start a general discussion"}
        </label>
        <textarea id="ns-general-discussion-draft" className="ws-textarea ws-feedback-input"
          maxLength={2000} rows={3} value={draft} onChange={e=>setDraft(e.target.value)} />
        <button type="submit" className="ns-button ns-button-primary" disabled={posting||!draft.trim()}>
          {fr?"Publier":"Post discussion"}
        </button>
      </form>}
    </div>}
  </details>;
}
